import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  COMPLIANCE_STATUS_OPTIONS,
  StatusDropdown,
} from '@/components/extraction/StatusDropdown'
import {
  effectiveProgress,
  PROGRESS_LABELS,
  type ProgressBucket,
} from '@/lib/compliance-progress'
import type { CoverageStatus } from '@/lib/api'
import type { ExtractedComplianceItem } from '@/types/extraction'

const BUCKET_VARIANT: Record<ProgressBucket, BadgeProps['variant']> = {
  not_started: 'default',
  in_progress: 'warning',
  in_review: 'review',
  done: 'success',
  no_action: 'secondary',
}

const DERIVED_TITLE = 'Derived from response coverage'

interface ProgressCellProps {
  item: ExtractedComplianceItem
  coverageStatus: CoverageStatus | undefined
  readOnly: boolean
  /** PATCH { status } — the server stamps statusSource 'manual'. */
  onStatusChange: (status: string) => void
  /**
   * PATCH { statusSource: 'derived' } — hands the row back to derivation.
   * Narrative rows only; omit it and the "Use derived" action disappears.
   */
  onHandBack?: (() => void) | undefined
  /** The drawer variant adds the "set manually" note and the hand-back action. */
  variant?: 'row' | 'drawer'
}

function ProgressPill({ bucket, derived }: { bucket: ProgressBucket; derived: boolean }) {
  return (
    <Badge
      variant={BUCKET_VARIANT[bucket]}
      dot
      className="text-xs font-normal whitespace-nowrap"
      {...(derived ? { title: DERIVED_TITLE } : {})}
    >
      {PROGRESS_LABELS[bucket]}
    </Badge>
  )
}

/**
 * The Progress control, shared by the table's Progress column and the
 * requirement drawer so the two can never disagree about what a row's progress
 * is or who owns it.
 *
 * Three shapes, decided by `effectiveProgress`:
 * - post-award rows have no progress at all, so nothing renders;
 * - derived rows show a read-only pill (the value belongs to response
 *   coverage), with the drawer offering a deliberate override;
 * - manual rows own their value and get the ordinary status dropdown.
 */
export function ProgressCell({
  item,
  coverageStatus,
  readOnly,
  onStatusChange,
  onHandBack,
  variant = 'row',
}: ProgressCellProps) {
  const { bucket, derived } = effectiveProgress(item, coverageStatus)
  if (bucket === null) return null

  if (readOnly) return <ProgressPill bucket={bucket} derived={derived} />

  if (derived) {
    if (variant !== 'drawer') return <ProgressPill bucket={bucket} derived />
    return (
      <div className="flex items-center gap-2">
        <ProgressPill bucket={bucket} derived />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 text-xs">
              Set manually
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {COMPLIANCE_STATUS_OPTIONS.map((option) => (
              <DropdownMenuItem key={option.value} onClick={() => onStatusChange(option.value)}>
                <span className={`mr-2 h-2 w-2 rounded-full ${option.dotColor}`} aria-hidden="true" />
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  const dropdown = (
    <StatusDropdown
      value={item.status}
      options={COMPLIANCE_STATUS_OPTIONS}
      onChange={onStatusChange}
    />
  )

  // The note and hand-back describe an explicit manual override, so they need
  // the stored provenance to say so — not merely a non-derived result.
  if (variant !== 'drawer' || !onHandBack || item.statusSource !== 'manual') return dropdown

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {dropdown}
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onHandBack}>
          Use derived
        </Button>
      </div>
      <p className="text-meta">Set manually. Progress no longer follows response coverage.</p>
    </div>
  )
}
